import React, { useEffect, useMemo } from "react";
import { DEFAULT_CONFIG, formatFileSize } from "../utils";
import { ATTACHMENT_FIELD_STYLES } from "./styles";
import { IAttachment } from "../../approval/approval-form/approval-form";
import axios from "axios";
import Auth from "../../../Auth/Auth";
import { FileUploadConfig } from "../types";
import { useLoading } from "../../../screens/components/loading-context";

interface SelectedFileListProps {
  files: FileList | null;
  disabled: boolean;
  onRemoveFile: (index: number) => void;
  attachments: IAttachment[];
}

// 공통 파일 인터페이스 정의
interface CommonFileItem {
  name: string;
  size: number;
  link?: string;
}

const SelectedFileList: React.FC<SelectedFileListProps> = ({
  files,
  disabled,
  onRemoveFile,
  attachments
}) => {
  if (!disabled && (!files || files.length === 0)) {
    return null;
  }

  const { setIsLoading, isLoading } = useLoading();

  useEffect(() => {
    if (isLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isLoading]);

  // 공통 타입으로 통일된 파일 리스트
  const fileList = useMemo<CommonFileItem[]>(() => {
    if (disabled) {
      return attachments.map(attachment => ({
        name: attachment.filename,
        size: attachment.filesize,
        link: attachment.link_address
      }));
    }
    return Array.from(files || []).map(file => ({
      name: file.name,
      size: file.size
    }));
  }, [disabled, attachments, files]);

  // 총 파일 크기 계산
  const getTotalFileSize = (fileItems: CommonFileItem[]): string => {
    const totalBytes = fileItems.reduce((total, file) => total + file.size, 0);
    return formatFileSize(totalBytes);
  };

  // 파일 다운로드 함수
  const handleFileDownload = async (fileUrl: string, fileName: string) => {
    try {
      setIsLoading(true);
      const config: FileUploadConfig = DEFAULT_CONFIG;
      const response = await axios.post(
        `${config.downloadUrl}`,
        { fileUrl },
        {
          headers: {
            token: Auth.getUserToken(),
            "Content-Type": "application/json"
          },
          responseType: "blob"
        }
      );

      // Blob을 사용하여 파일 다운로드
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("파일 다운로드 오류:", error);
      alert("파일 다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={ATTACHMENT_FIELD_STYLES.fileListContainer}>
      <div style={ATTACHMENT_FIELD_STYLES.fileListHeader}>
        첨부된 파일 ({fileList.length}개) - 총 크기:{" "}
        {getTotalFileSize(fileList)}
      </div>
      {fileList.map((file, index) => (
        <div key={index} style={ATTACHMENT_FIELD_STYLES.fileItem}>
          <div style={ATTACHMENT_FIELD_STYLES.fileDetails}>
            <div style={ATTACHMENT_FIELD_STYLES.fileName}>
              {file.link ? (
                <button
                  type="button"
                  onClick={() => handleFileDownload(file.link!, file.name)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#0066cc",
                    textDecoration: "none",
                    cursor: "pointer",
                    padding: 0,
                    font: "inherit"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.textDecoration = "underline";
                    e.currentTarget.style.color = "#0052a3";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.textDecoration = "none";
                    e.currentTarget.style.color = "#0066cc";
                  }}
                >
                  {file.name}
                </button>
              ) : (
                file.name
              )}
            </div>
            <div style={ATTACHMENT_FIELD_STYLES.fileSize}>
              {formatFileSize(file.size)}
            </div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onRemoveFile(index)}
              style={ATTACHMENT_FIELD_STYLES.removeButton}
              title="파일 제거"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default SelectedFileList;
